import React, { useState, useEffect } from 'react';
import adminService from '../../services/api/admin';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { getArabicDate } from '../../services/utils/date';
import { 
  CreditCard, 
  Key, 
  CheckCircle, 
  XCircle, 
  Clock, 
  Plus,
  Search,
  Eye,
  Calendar,
  DollarSign,
  ShieldCheck,
  ChevronLeft,
  Ticket,
  UserCheck,
  Smartphone,
  X,
  Save,
  Info,
  CheckCircle2,
  Layers,
  LayoutDashboard,
  Sparkles
} from 'lucide-react';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { useToast } from '../../components/common/Toast';
import { getPaymentScreenshotUrl } from '../../config/apiConfig';
import { useConfirm } from '../../hooks/useConfirm';

const SubscriptionManagement = () => {
  const queryClient = useQueryClient();
  const { confirm, ConfirmDialogComponent } = useConfirm();
  const toast = useToast();
  const [activeTab, setActiveTab] = useState('requests');
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [showCreateCodesModal, setShowCreateCodesModal] = useState(false);
  const [showDurationModal, setShowDurationModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [selectedDuration, setSelectedDuration] = useState(null); // number | null
  const [newCodesForm, setNewCodesForm] = useState({
    courseId: '',
    count: 1,
    expiresAt: '',
    durationDays: 30,
    price: 0
  });

  // جلب طلبات الدفع (المنطق الأصلي)
  const { data: paymentRequests, isLoading: requestsLoading } = useQuery(
    ['payment-requests', statusFilter],
    () => adminService.getPaymentRequests({ status: statusFilter })
  );

  // جلب الأكواد
  const { data: activationCodes, isLoading: codesLoading } = useQuery(
    'activation-codes',
    () => adminService.getActivationCodes()
  );

  // جلب الكورسات
  const { data: courses = [] } = useQuery(
    'courses',
    () => adminService.getCourses(),
    {
      select: (data) => {
        // Extract courses array from response object
        return Array.isArray(data) ? data : (data?.courses || []);
      },
      // Provide a default value to ensure courses is always an array
      placeholderData: [],
    }
  );

  // تحديث السعر والمدة تلقائياً عند تغيير الكورس
  useEffect(() => {
    if (newCodesForm.courseId && courses) {
      const selectedCourse = courses.find(course => course.id === parseInt(newCodesForm.courseId));
      if (selectedCourse) {
        setNewCodesForm(prev => ({ 
          ...prev, 
          price: selectedCourse.price || 0,
          durationDays: selectedCourse.default_duration_days || 30
        }));
      }
    }
  }, [newCodesForm.courseId, courses]);

  // تحديث حالة طلب الدفع
  const updateRequestMutation = useMutation(
    async ({ id, status, adminNotes, duration_days }) => {
      const response = await adminService.updatePaymentRequestStatus(id, { status, adminNotes, duration_days });
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries('payment-requests');
        toast.success('تم تحديث حالة الطلب بنجاح');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'فشل في تحديث الحالة');
      }
    }
  );

  // إنشاء أكواد جديدة
  const createCodesMutation = useMutation(
    async (data) => {
      const response = await adminService.createActivationCodes(data);
      return response.data;
    },
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries('activation-codes');
        toast.success(data.message || 'تم توليد الأكواد بنجاح');
        setShowCreateCodesModal(false);
        setNewCodesForm({ courseId: '', count: 1, expiresAt: '', durationDays: 30, price: 0 });
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'فشل في إنشاء الأكواد');
      }
    }
  );

  const handleUpdateRequest = async (request, newStatus) => {
    if (newStatus === 'approved') {
      setSelectedRequest(request);
      setShowDurationModal(true);
      return;
    }
    
    const adminNotes = 'تم رفض الطلب لعدم وضوح البيانات';
    const confirmed = await confirm({
      title: 'تأكيد الرفض الإداري',
      message: `هل أنت متأكد من رفض طلب الطالب "${request.student_name}"؟ سيتم إعلامه بالرفض.`,
      type: 'danger'
    });
    if (!confirmed) return;

    updateRequestMutation.mutate({ id: request.id, status: newStatus, adminNotes });
  };

  const handleApproveWithDuration = async () => {
    const finalDuration = selectedDuration ?? selectedRequest.default_duration_days;
    
    if (!finalDuration || finalDuration <= 0) {
      return toast.error('مدة الاشتراك غير صالحة');
    }
    
    let adminNotes;
    
    if (!selectedDuration) {
      // استخدم مدة الكورس الافتراضية
      const courseDuration = selectedRequest.default_duration_days || 30;
      const durationText = courseDuration === 30 ? 'اشتراك شهري' : 
                         courseDuration === 120 ? 'اشتراك ترم دراسي' : 
                         courseDuration === 300 ? 'اشتراك عام دراسي' : 
                         courseDuration === 365 ? 'سنة كاملة' : 'اشتراك شهري';
      adminNotes = `تم الموافقة على الطلب - مدة الاشتراك: مدة الكورس الافتراضية (${durationText})`;
    } else {
      const durationText = finalDuration === 30 ? 'اشتراك شهري' : 
                         finalDuration === 120 ? 'اشتراك ترم دراسي' : 
                         finalDuration === 300 ? 'اشتراك عام دراسي' : 
                         finalDuration === 365 ? 'سنة كاملة' : `${finalDuration} يوم`;
      adminNotes = `تم الموافقة على الطلب - مدة الاشتراك: ${durationText}`;
    }
    
    updateRequestMutation.mutate({ 
      id: selectedRequest.id, 
      status: 'approved', 
      adminNotes,
      duration_days: finalDuration 
    });
    setShowDurationModal(false);
    setSelectedRequest(null);
    setSelectedDuration(null);
  };

  const handleCreateCodes = (e) => {
    e.preventDefault();
    if (!newCodesForm.courseId) return toast.error('يرجى اختيار الكورس المستهدف');
    if (newCodesForm.count < 1 || newCodesForm.count > 100) return toast.error('عدد الأكواد المسموح به بين 1 و 100');
    
    // Send duration_days to backend
    const formData = {
      ...newCodesForm,
      duration_days: newCodesForm.durationDays
    };
    
    createCodesMutation.mutate(formData);
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: 'bg-amber-50 text-amber-700 border-amber-100',
      approved: 'bg-emerald-50 text-emerald-700 border-emerald-100',
      rejected: 'bg-rose-50 text-rose-700 border-rose-100'
    };
    return badges[status] || 'bg-gray-50 text-gray-700 border-gray-100';
  };

  const getStatusText = (status) => {
    const texts = { pending: 'قيد المراجعة', approved: 'موافق عليه', rejected: 'مرفوض إدارياً' };
    return texts[status] || status;
  };

  const filteredRequests = paymentRequests?.data?.requests?.filter(request => 
    request.student_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    request.phone?.includes(searchTerm) ||
    request.course_title?.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  const filteredCodes = activationCodes?.data?.codes?.filter(code => 
    code.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
    code.course_title.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  const stats = {
    totalRequests: paymentRequests?.data?.requests?.length || 0,
    pendingRequests: paymentRequests?.data?.requests?.filter(r => r.status === 'pending').length || 0,
    approvedRequests: paymentRequests?.data?.requests?.filter(r => r.status === 'approved').length || 0,
    totalCodes: activationCodes?.data?.codes?.length || 0,
    usedCodes: activationCodes?.data?.codes?.filter(c => c.is_used).length || 0,
    availableCodes: activationCodes?.data?.codes?.filter(c => !c.is_used).length || 0
  };

  if (requestsLoading || codesLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-24 min-h-[500px]">
        <LoadingSpinner size="large" />
        <p className="mt-4 text-gray-500 font-bold animate-pulse uppercase tracking-widest text-xs">جاري تحميل سجل الاشتراكات...</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-10 px-4 sm:px-6 py-6 min-h-screen bg-gray-50/30">
      
      {/* Header Section */}
      <div className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 p-8 lg:p-10 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-32 h-32 bg-primary-50 rounded-br-[5rem] opacity-40"></div>
        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="space-y-1">
            <h1 className="text-3xl lg:text-4xl font-black text-gray-900 tracking-tight flex items-center gap-3">
                <ShieldCheck className="text-primary-600" size={32} />
                إدارة نظام الاشتراكات
            </h1>
            <p className="text-gray-500 font-medium flex items-center gap-2">
                <LayoutDashboard size={18} className="text-emerald-500" />
                معالجة مدفوعات الطلاب وتوليد أكواد التفعيل الذكية
            </p>
          </div>
          <div className="bg-primary-600 text-white px-8 py-4 rounded-2xl shadow-xl shadow-primary-100 flex items-center gap-4">
              <div className="bg-white/20 p-2 rounded-xl"><DollarSign size={24} /></div>
              <div>
                  <p className="text-[10px] font-black uppercase opacity-80 leading-none mb-1">إجمالي العمليات</p>
                  <p className="text-2xl font-black leading-none">{stats.totalRequests + stats.totalCodes}</p>
              </div>
          </div>
        </div>
      </div>

      {/* Dynamic Statistics Pills Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {[
          { label: 'إجمالي الطلبات', value: stats.totalRequests, icon: CreditCard, color: 'text-blue-600', bg: 'bg-blue-50' },
          { label: 'قيد المراجعة', value: stats.pendingRequests, icon: Clock, color: 'text-amber-600', bg: 'bg-amber-50', urgent: stats.pendingRequests > 0 },
          { label: 'طلبات مقبولة', value: stats.approvedRequests, icon: CheckCircle2, color: 'text-emerald-600', bg: 'bg-emerald-50' },
          { label: 'إجمالي الأكواد', value: stats.totalCodes, icon: Ticket, color: 'text-purple-600', bg: 'bg-purple-50' },
          { label: 'أكواد مستخدمة', value: stats.usedCodes, icon: UserCheck, color: 'text-gray-600', bg: 'bg-gray-100' },
          { label: 'أكواد متاحة', value: stats.availableCodes, icon: Key, color: 'text-primary-600', bg: 'bg-primary-50' }
        ].map((stat, i) => (
          <div key={i} className={`bg-white p-5 rounded-[1.8rem] border shadow-sm flex flex-col items-center text-center group hover:shadow-md transition-all ${stat.urgent ? 'border-amber-200 ring-4 ring-amber-50' : 'border-gray-100'}`}>
            <div className={`p-3 ${stat.bg} ${stat.color} rounded-2xl mb-3 group-hover:scale-110 transition-transform`}>
              <stat.icon size={20} />
            </div>
            <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest leading-tight mb-1">{stat.label}</p>
            <p className="text-xl font-black text-gray-900">{stat.value}</p>
          </div>
        ))}
      </div>

      {/* Tab Switcher & Action Bar */}
      <div className="bg-white rounded-[2rem] shadow-sm border border-gray-100 p-4">
        <div className="flex flex-col lg:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-2 bg-gray-50 p-1.5 rounded-2xl border border-gray-100 w-full lg:w-fit">
            <button
              onClick={() => setActiveTab('requests')}
              className={`flex-1 lg:flex-none flex items-center justify-center gap-2 px-8 py-3 rounded-xl font-black text-sm transition-all duration-300 ${
                activeTab === 'requests' ? 'bg-white text-primary-600 shadow-md' : 'text-gray-400 hover:text-gray-600'
              }`}
            >
              <CreditCard size={18} />
              طلبات الدفع
            </button>
            <button
              onClick={() => setActiveTab('codes')}
              className={`flex-1 lg:flex-none flex items-center justify-center gap-2 px-8 py-3 rounded-xl font-black text-sm transition-all duration-300 ${
                activeTab === 'codes' ? 'bg-white text-primary-600 shadow-md' : 'text-gray-400 hover:text-gray-600'
              }`}
            >
              <Key size={18} />
              أكواد التفعيل
            </button>
          </div>

          <div className="flex items-center gap-4 w-full lg:w-auto">
            <div className="relative group flex-1 lg:w-80">
                <Search className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-primary-500 transition-colors w-5 h-5" />
                <input
                    type="text"
                    placeholder="بحث سريع بالاسم، الرقم، أو الكود..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pr-12 pl-4 py-3.5 bg-gray-50 border border-transparent rounded-2xl focus:bg-white focus:ring-4 focus:ring-primary-500/10 focus:border-primary-500 transition-all font-medium"
                />
            </div>
            {activeTab === 'requests' && (
                <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="hidden md:block px-4 py-3.5 bg-white border border-gray-100 rounded-2xl font-bold text-gray-600 focus:outline-none focus:ring-4 focus:ring-primary-500/10 cursor-pointer shadow-sm"
                >
                    <option value="all">جميع الحالات</option>
                    <option value="pending">قيد المراجعة</option>
                    <option value="approved">موافق عليه</option>
                    <option value="rejected">مرفوض</option>
                </select>
            )}
            {activeTab === 'codes' && (
                <button
                onClick={() => setShowCreateCodesModal(true)}
                className="bg-primary-600 text-white px-8 py-3.5 rounded-2xl font-black text-sm hover:bg-primary-700 transition-all shadow-xl shadow-primary-100 flex items-center gap-2 transform active:scale-95"
                >
                    <Plus size={20} /> توليد أكواد
                </button>
            )}
          </div>
        </div>
      </div>

      {/* Main Content Table Area */}
      <div className="bg-white rounded-[2.5rem] shadow-sm border border-gray-100 overflow-hidden group transition-all duration-500 hover:shadow-xl">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-50">
            <thead className="bg-gray-50/50">
              {activeTab === 'requests' ? (
                <tr>
                  <th className="px-8 py-6 text-right text-[10px] font-black text-gray-400 uppercase tracking-widest">بيانات الطالب</th>
                  <th className="px-8 py-6 text-right text-[10px] font-black text-gray-400 uppercase tracking-widest">الكورس المستهدف</th>
                  <th className="px-8 py-6 text-right text-[10px] font-black text-gray-400 uppercase tracking-widest">رقم المعاملة</th>
                  <th className="px-8 py-6 text-right text-[10px] font-black text-gray-400 uppercase tracking-widest">الحالة</th>
                  <th className="px-8 py-6 text-right text-[10px] font-black text-gray-400 uppercase tracking-widest">تاريخ الطلب</th>
                  <th className="px-8 py-6 text-left text-[10px] font-black text-gray-400 uppercase tracking-widest">الإجراءات</th>
                </tr>
              ) : (
                <tr>
                  <th className="px-8 py-6 text-right text-[10px] font-black text-gray-400 uppercase tracking-widest">الكود التسلسلي</th>
                  <th className="px-8 py-6 text-right text-[10px] font-black text-gray-400 uppercase tracking-widest">الكورس المخصص</th>
                  <th className="px-8 py-6 text-right text-[10px] font-black text-gray-400 uppercase tracking-widest">حالة الاستخدام</th>
                  <th className="px-8 py-6 text-right text-[10px] font-black text-gray-400 uppercase tracking-widest">اسم المستخدم</th>
                  <th className="px-8 py-6 text-right text-[10px] font-black text-gray-400 uppercase tracking-widest">تاريخ الإنشاء</th>
                  <th className="px-8 py-6 text-right text-[10px] font-black text-gray-400 uppercase tracking-widest">الصلاحية</th>
                </tr>
              )}
            </thead>
            <tbody className="bg-white divide-y divide-gray-50">
              {activeTab === 'requests' ? (
                filteredRequests.map((request) => (
                  <tr key={request.id} className="hover:bg-primary-50/30 transition-colors group/row">
                    <td className="px-8 py-6 whitespace-nowrap">
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-700 rounded-xl flex items-center justify-center text-white font-black shadow-md group-hover/row:scale-110 transition-transform">
                          {request.student_name?.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <div className="text-sm font-black text-gray-900 group-hover/row:text-primary-600 transition-colors">{request.student_name}</div>
                          <div className="flex items-center gap-1 text-[10px] font-bold text-gray-400 uppercase tracking-tighter">
                            <Smartphone size={10} /> {request.phone}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-8 py-6 whitespace-nowrap">
                      <span className="bg-gray-100 text-gray-700 px-3 py-1 rounded-lg text-xs font-bold">{request.course_title}</span>
                    </td>
                    <td className="px-8 py-6 whitespace-nowrap">
                      <div className="text-xs font-black font-mono text-gray-600 bg-gray-50 px-2 py-1 rounded border border-gray-100 w-fit">{request.transaction_number}</div>
                    </td>
                    <td className="px-8 py-6 whitespace-nowrap">
                      <span className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-xl text-[10px] font-black border tracking-widest uppercase ${getStatusBadge(request.status)}`}>
                        {request.status === 'pending' ? <Clock size={12} /> : request.status === 'approved' ? <CheckCircle2 size={12} /> : <XCircle size={12} />}
                        {getStatusText(request.status)}
                      </span>
                    </td>
                    <td className="px-8 py-6 whitespace-nowrap">
                        <div className="flex items-center gap-2 text-xs font-bold text-gray-500">
                            <Calendar size={14} className="text-primary-500" />
                            {getArabicDate(request.created_at)}
                        </div>
                    </td>
                    <td className="px-8 py-6 whitespace-nowrap text-left">
                      <div className="flex items-center justify-end gap-2 opacity-0 group-hover/row:opacity-100 transition-opacity">
                        {request.screenshot_path && (
                          <button
                            onClick={() => {
                              const imageUrl = getPaymentScreenshotUrl(request.screenshot_path);
                              setSelectedImage(imageUrl);
                              setShowImageModal(true);
                            }}
                            className="w-10 h-10 flex items-center justify-center bg-white border border-gray-100 text-blue-600 rounded-xl hover:bg-blue-600 hover:text-white transition-all shadow-sm"
                            title="معاينة إثبات الدفع"
                          >
                            <Eye size={18} />
                          </button>
                        )}
                        
                        {request.status === 'pending' && (
                          <>
                            <button
                              onClick={() => handleUpdateRequest(request, 'approved')}
                              className="w-10 h-10 flex items-center justify-center bg-white border border-emerald-100 text-emerald-600 rounded-xl hover:bg-emerald-600 hover:text-white transition-all shadow-sm"
                              title="قبول الاشتراك"
                            >
                              <CheckCircle size={18} />
                            </button>
                            <button
                              onClick={() => handleUpdateRequest(request, 'rejected')}
                              className="w-10 h-10 flex items-center justify-center bg-white border border-rose-100 text-rose-600 rounded-xl hover:bg-rose-600 hover:text-white transition-all shadow-sm"
                              title="رفض الطلب"
                            >
                              <XCircle size={18} />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                (filteredCodes || []).map((code) => (
                  <tr key={code.id} className="hover:bg-primary-50/30 transition-colors group/row">
                    <td className="px-8 py-6 whitespace-nowrap">
                      <div className="text-sm font-black font-mono text-primary-600 bg-primary-50 px-3 py-1.5 rounded-xl border border-primary-100 w-fit group-hover/row:bg-primary-600 group-hover/row:text-white transition-all">
                        {code.code}
                      </div>
                    </td>
                    <td className="px-8 py-6 whitespace-nowrap text-sm font-bold text-gray-700">
                      {code.course_title}
                    </td>
                    <td className="px-8 py-6 whitespace-nowrap">
                      <span className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-xl text-[10px] font-black border tracking-widest uppercase ${
                        code.is_used 
                          ? 'bg-rose-50 text-rose-700 border-rose-100' 
                          : 'bg-emerald-50 text-emerald-700 border-emerald-100 shadow-sm'
                      }`}>
                        {code.is_used ? <CheckCircle2 size={12} /> : <Sparkles size={12} />}
                        {code.is_used ? 'مستخدم مسبقاً' : 'كود متاح'}
                      </span>
                    </td>
                    <td className="px-8 py-6 whitespace-nowrap text-sm font-black text-gray-900">
                      {code.used_by_name || <span className="text-gray-300 italic font-medium">بانتظار طالب...</span>}
                    </td>
                    <td className="px-8 py-6 whitespace-nowrap text-xs font-bold text-gray-500">
                      {getArabicDate(code.created_at)}
                    </td>
                    <td className="px-8 py-6 whitespace-nowrap text-xs font-bold text-gray-500">
                      {code.expires_at ? (
                          <div className="flex items-center gap-2">
                             <Clock size={12} className="text-rose-400" />
                             {getArabicDate(code.expires_at)}
                          </div>
                      ) : <span className="text-emerald-500">دائم</span>}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Empty State Illustration */}
      {((activeTab === 'requests' && filteredRequests.length === 0) || (activeTab === 'codes' && filteredCodes.length === 0)) && (
          <div className="bg-white rounded-[3rem] p-20 text-center border border-dashed border-gray-200">
              <div className="w-24 h-24 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-6">
                  {activeTab === 'requests' ? <CreditCard size={48} className="text-gray-200" /> : <Key size={48} className="text-gray-200" />}
              </div>
              <h3 className="text-2xl font-black text-gray-900 mb-2">لا يوجد بيانات لعرضها</h3>
              <p className="text-gray-500 max-w-sm mx-auto font-medium">
                  {searchTerm ? 'لم يتم العثور على نتائج تطابق بحثك الحالي.' : activeTab === 'requests' ? 'لا يوجد طلبات دفع بانتظار المراجعة حالياً.' : 'لم يتم توليد أي أكواد تفعيل لهذا الكورس بعد.'}
              </p>
          </div>
      )}

      {/* Create Codes Modern Modal */}
      {showCreateCodesModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-[300] p-4 animate-in fade-in duration-300">
          <div className="bg-white rounded-[2.5rem] shadow-2xl max-w-md w-full relative overflow-hidden">
            <div className="flex items-center justify-between p-8 border-b border-gray-100 bg-gray-50/50">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-primary-600 rounded-2xl flex items-center justify-center text-white shadow-xl shadow-primary-100">
                    <Key size={24} />
                </div>
                <div>
                    <h3 className="text-xl font-black text-gray-900 leading-none">توليد أكواد ذكية</h3>
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-1.5">إنشاء رموز تفعيل للكورسات</p>
                </div>
              </div>
              <button onClick={() => setShowCreateCodesModal(false)} className="w-10 h-10 flex items-center justify-center bg-white text-gray-400 hover:bg-red-50 hover:text-red-500 rounded-xl transition-all border border-gray-100">
                <X size={20} />
              </button>
            </div>
            
            <form onSubmit={handleCreateCodes} className="p-8 space-y-8">
              <div className="space-y-6">
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mr-2 tracking-widest">الكورس المستهدف *</label>
                    <div className="relative">
                        <select
                        value={newCodesForm.courseId}
                        onChange={(e) => {
                          const selectedCourse = courses.find(course => course.id === Number(e.target.value));
                          setNewCodesForm(prev => ({ 
                            ...prev, 
                            courseId: e.target.value, 
                            durationDays: selectedCourse?.default_duration_days || 30 
                          }));
                        }}
                        className="w-full px-5 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white font-bold text-gray-700 appearance-none cursor-pointer"
                        required
                        >
                        <option value="">اختر الكورس التعليمي...</option>
                        {courses?.map(course => {
                            const durationText = !course.default_duration_days ? 'غير محدد' :
                                              course.default_duration_days === 30 ? 'اشتراك شهري' :
                                              course.default_duration_days === 120 ? 'اشتراك ترم دراسي' :
                                              course.default_duration_days === 300 ? 'اشتراك عام دراسي' :
                                              `${course.default_duration_days} يوم`;
                            return (
                              <option key={course.id} value={course.id}>
                                {course.title} - ({durationText})
                              </option>
                            );
                          })}
                        </select>
                        <ChevronLeft className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 -rotate-90" size={18} />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-6">
                    <div className="space-y-2">
                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mr-2">عدد الأكواد (1-100)</label>
                        <div className="relative">
                            <input
                            type="number"
                            min="1"
                            max="100"
                            value={newCodesForm.count}
                            onChange={(e) => setNewCodesForm(prev => ({ ...prev, count: parseInt(e.target.value) }))}
                            className="w-full px-5 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white font-black text-gray-900"
                            required
                            />
                            <Layers className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300" size={16} />
                        </div>
                    </div>

                    <div className="space-y-2">
                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mr-2">مدة الكود (أيام)</label>
                        <div className="relative">
                            <select
                            value={newCodesForm.durationDays}
                            onChange={(e) => setNewCodesForm(prev => ({ ...prev, durationDays: parseInt(e.target.value) }))}
                            className="w-full px-5 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white font-bold text-gray-700 appearance-none cursor-pointer"
                            >
                            <option value={30}>30 يوم (اشتراك شهري)</option>
                            <option value={120}>120 يوم (اشتراك ترم دراسي)</option>
                            <option value={300}>300 يوم (اشتراك عام دراسي)</option>
                            </select>
                            <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300" size={16} />
                        </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-6">
                    <div className="space-y-2">
                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mr-2">تاريخ الانتهاء</label>
                        <div className="relative">
                            <input
                            type="date"
                            value={newCodesForm.expiresAt}
                            onChange={(e) => setNewCodesForm(prev => ({ ...prev, expiresAt: e.target.value }))}
                            className="w-full px-5 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white font-bold text-gray-700"
                            />
                        </div>
                    </div>

                    <div className="space-y-2">
                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest mr-2">السعر (ج.م)</label>
                        <div className="relative">
                            <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={newCodesForm.price}
                            onChange={(e) => setNewCodesForm(prev => ({ ...prev, price: parseFloat(e.target.value) }))}
                            className="w-full px-5 py-4 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white font-black text-gray-900"
                            />
                            <DollarSign className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300" size={16} />
                        </div>
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-tighter">يتم جلب السعر والمدة تلقائياً من الكورس مع إمكانية التعديل</p>
                    </div>
                  </div>
              </div>

              <div className="bg-blue-50 p-4 rounded-2xl border border-blue-100 flex items-start gap-3">
                  <Info size={20} className="text-blue-500 mt-0.5" />
                  <p className="text-[10px] font-bold text-blue-700 leading-relaxed uppercase tracking-tighter">سيتم إنشاء الأكواد بشكل عشوائي وفريد لكل نسخة، يمكنك تصديرها لاحقاً لملف اكسيل.</p>
              </div>

              <div className="grid grid-cols-2 gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateCodesModal(false)}
                  className="px-6 py-4 border border-gray-100 rounded-2xl text-gray-400 font-black text-sm hover:bg-gray-50 transition-all uppercase tracking-widest"
                >
                  إلغاء
                </button>
                <button
                  type="submit"
                  disabled={createCodesMutation.isLoading}
                  className="px-6 py-4 bg-primary-600 text-white rounded-2xl font-black text-sm shadow-xl shadow-primary-100 hover:bg-primary-700 transition-all disabled:opacity-50 transform active:scale-95 flex items-center justify-center gap-2"
                >
                  {createCodesMutation.isLoading ? <LoadingSpinner size="small" /> : <Save size={18} />}
                  تأكيد التوليد
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Image Preview Modal */}
      {showImageModal && selectedImage && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[400] p-4 animate-in fade-in duration-300">
          <div className="relative max-w-4xl max-h-[90vh] w-full">
            <div className="bg-white rounded-[2rem] shadow-2xl overflow-hidden">
              <div className="flex items-center justify-between p-6 border-b border-gray-100 bg-gray-50/50">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary-600 rounded-xl flex items-center justify-center text-white shadow-lg">
                    <Eye size={20} />
                  </div>
                  <div>
                    <h3 className="text-lg font-black text-gray-900 leading-none">معاينة إثبات الدفع</h3>
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-1">Payment Screenshot Preview</p>
                  </div>
                </div>
                <button 
                  onClick={() => setShowImageModal(false)}
                  className="w-10 h-10 flex items-center justify-center bg-white text-gray-400 hover:bg-red-50 hover:text-red-500 rounded-xl transition-all border border-gray-100 shadow-sm"
                >
                  <X size={20} />
                </button>
              </div>
              
              <div className="p-6 bg-gray-50/30">
                <div className="relative bg-white rounded-2xl shadow-inner p-4">
                  <img 
                    src={selectedImage} 
                    alt="Payment Screenshot" 
                    className="w-full h-auto max-h-[60vh] object-contain rounded-xl"
                    onError={(e) => {
                      e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik04MCA2MEgxMjBWODBIODBWNjBaIiBmaWxsPSIjQ0NDIi8+CjxwYXRoIGQ9Ik04MCA5MEgxMjBWMTIwSDgwVjkwWiIgZmlsbD0iI0NDQyIvPgo8cGF0aCBkPSJNODAgMTIwSDEyMFYxNjBIODBWMTIwWiIgZmlsbD0iI0NDQyIvPgo8L3N2Zz4K';
                    }}
                  />
                </div>
                
                <div className="flex justify-between items-center mt-6">
                  <div className="text-sm text-gray-500">
                    <span className="font-medium">URL:</span>
                    <code className="ml-2 text-xs bg-gray-100 px-2 py-1 rounded">{selectedImage}</code>
                  </div>
                  <div className="flex gap-3">
                    <button
                      onClick={() => window.open(selectedImage, '_blank')}
                      className="px-4 py-2 bg-primary-600 text-white rounded-xl font-black text-sm hover:bg-primary-700 transition-all flex items-center gap-2 shadow-lg"
                    >
                      <Eye size={16} />
                      فتح في تاب جديد
                    </button>
                    <button
                      onClick={() => setShowImageModal(false)}
                      className="px-4 py-2 bg-gray-200 text-gray-700 rounded-xl font-black text-sm hover:bg-gray-300 transition-all"
                    >
                      إغلاق
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Duration Selection Modal */}
      {showDurationModal && selectedRequest && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-[300] p-4 animate-in fade-in duration-300">
          <div className="bg-white rounded-[2.5rem] shadow-2xl max-w-md w-full relative overflow-hidden">
            <div className="flex items-center justify-between p-8 border-b border-gray-100 bg-gray-50/50">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-emerald-600 rounded-2xl flex items-center justify-center text-white shadow-xl shadow-emerald-100">
                    <CheckCircle size={24} />
                </div>
                <div>
                    <h3 className="text-xl font-black text-gray-900 leading-none">قبول طلب الدفع</h3>
                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-1.5">تحديد مدة الاشتراك</p>
                </div>
              </div>
              <button onClick={() => setShowDurationModal(false)} className="w-10 h-10 flex items-center justify-center bg-white text-gray-400 hover:bg-red-50 hover:text-red-500 rounded-xl transition-all border border-gray-100">
                <X size={20} />
              </button>
            </div>
            
            <div className="p-8 space-y-6">
              <div className="space-y-3">
                <p className="text-xs font-bold text-blue-900">الطالب: <span className="text-blue-700">{selectedRequest.student_name}</span></p>
                <p className="text-xs font-bold text-blue-900">الكورس: <span className="text-blue-700">{selectedRequest.course_title}</span></p>
                <p className="text-xs font-bold text-gray-600">
                  مدة الكورس: 
                  <span className="text-gray-700 mr-1">
                    {selectedRequest.default_duration_days
                      ? {
                          30: 'اشتراك شهري',
                          120: 'اشتراك ترم دراسي',
                          300: 'اشتراك عام دراسي',
                          365: 'سنة كاملة'
                        }[selectedRequest.default_duration_days] ||
                        `${selectedRequest.default_duration_days} يوم` 
                      : 'غير محدد'}
                  </span>
                </p>
              </div>

              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-[9px] font-black text-gray-400 uppercase tracking-widest mr-2">مدة الاشتراك (أيام)</label>
                  <div className="relative">
                    <select
                      value={selectedDuration ?? ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        setSelectedDuration(value ? Number(value) : null);
                      }}
                      className="w-full px-4 py-3.5 bg-gray-50 border border-gray-100 rounded-2xl focus:ring-4 focus:ring-primary-500/10 focus:bg-white font-bold text-gray-700 appearance-none cursor-pointer text-sm"
                    >
                      <option value="">استخدام مدة الكورس</option>
                      <option value={30}>30 يوم (اشتراك شهري)</option>
                      <option value={120}>120 يوم (اشتراك ترم دراسي)</option>
                      <option value={300}>300 يوم (اشتراك عام دراسي)</option>
                      <option value={365}>365 يوم (سنة كاملة)</option>
                    </select>
                    <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-300" size={14} />
                  </div>
                </div>
              </div>

              {selectedDuration === null && (
                <p className="text-xs text-blue-600 font-medium bg-blue-50 p-2 rounded-lg">
                  سيتم استخدام مدة الكورس الافتراضية تلقائياً
                </p>
              )}

              <div className="grid grid-cols-2 gap-4 pt-4">
                <button
                  onClick={() => setShowDurationModal(false)}
                  className="px-6 py-4 border border-gray-100 rounded-2xl text-gray-400 font-black text-sm hover:bg-gray-50 transition-all uppercase tracking-widest"
                >
                  إلغاء
                </button>
                <button
                  onClick={handleApproveWithDuration}
                  className="px-6 py-4 bg-emerald-600 text-white rounded-2xl font-black text-sm shadow-xl shadow-emerald-100 hover:bg-emerald-700 transition-all transform active:scale-95 flex items-center justify-center gap-2"
                >
                  <CheckCircle size={18} />
                  قبول الطلب
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <ConfirmDialogComponent />
    </div>
  );
};

export default SubscriptionManagement;